import { useEffect, useRef } from 'react';
import type { Bar, Quote } from '../../data/protocol/types';
import { formatNumber } from '../../shared/format';

interface PriceCanvasProps {
  bars: Bar[];
  symbol: string;
  quote: Quote;
}

type Point = {
  x: number;
  y: number;
  price: number;
};

type HoverState = {
  active: boolean;
  x: number;
  y: number;
};

type ChartBox = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

export function PriceCanvas({ bars, symbol, quote }: PriceCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const barsRef = useRef(bars);
  const symbolRef = useRef(symbol);
  const quoteRef = useRef(quote);
  const hoverRef = useRef<HoverState>({ active: false, x: 0, y: 0 });
  const frameRef = useRef<number>();

  const schedule = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = undefined;
      draw(
        canvas,
        barsRef.current,
        symbolRef.current,
        quoteRef.current,
        hoverRef.current
      );
    });
  };

  useEffect(() => {
    barsRef.current = bars;
    symbolRef.current = symbol;
    quoteRef.current = quote;
    schedule();
  }, [bars, symbol, quote]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateHover = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();

      hoverRef.current = {
        active: true,
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };

      schedule();
    };

    const clearHover = () => {
      hoverRef.current = { active: false, x: 0, y: 0 };
      schedule();
    };

    const resizeObserver = new ResizeObserver(schedule);

    resizeObserver.observe(canvas);
    canvas.addEventListener('pointermove', updateHover);
    canvas.addEventListener('pointerdown', updateHover);
    canvas.addEventListener('pointerleave', clearHover);
    canvas.addEventListener('pointercancel', clearHover);

    schedule();

    return () => {
      resizeObserver.disconnect();
      canvas.removeEventListener('pointermove', updateHover);
      canvas.removeEventListener('pointerdown', updateHover);
      canvas.removeEventListener('pointerleave', clearHover);
      canvas.removeEventListener('pointercancel', clearHover);

      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="price-canvas"
      aria-label={`${symbol} price chart`}
    />
  );
}

function draw(
  canvas: HTMLCanvasElement,
  bars: Bar[],
  symbol: string,
  quote: Quote,
  hover: HoverState
): void {
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const width = Math.max(1, Math.floor(rect.width * dpr));
  const height = Math.max(1, Math.floor(rect.height * dpr));

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.save();
  ctx.scale(dpr, dpr);

  const w = rect.width;
  const h = rect.height;

  const styles = getComputedStyle(document.documentElement);
  const bg = styles.getPropertyValue('--bg-window').trim() || '#0d1117';
  const grid = styles.getPropertyValue('--border-subtle').trim() || '#232a35';
  const text = styles.getPropertyValue('--text-muted').trim() || '#8b949e';
  const primary = styles.getPropertyValue('--text-primary').trim() || '#e6edf3';
  const accent = styles.getPropertyValue('--accent').trim() || '#7c9cff';
  const up = styles.getPropertyValue('--quote-up').trim() || '#31d158';
  const down = styles.getPropertyValue('--quote-down').trim() || '#ff453a';

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  const box = createChartBox(w, h);

  drawGrid(ctx, {
    box,
    color: grid
  });

  ctx.fillStyle = primary;
  ctx.font = '600 12px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(symbol, box.left, 18);

  const livePrice = quote.last > 0
    ? quote.last
    : bars[bars.length - 1]?.close ?? 0;

  if (bars.length < 2 || livePrice <= 0) {
    ctx.fillStyle = text;
    ctx.font = '11px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
    ctx.fillText('Waiting for bars…', box.left, box.top + 20);
    ctx.restore();
    return;
  }

  const history = bars.map((bar) => bar.close);
  const display = smoothCloses(history);

  // Quote store owns the live price. Historical bars draw the path.
  display[display.length - 1] = livePrice;

  const minClose = Math.min(...display);
  const maxClose = Math.max(...display);
  const rawRange = maxClose - minClose || Math.max(maxClose * 0.01, 1);
  const paddingY = rawRange * 0.12;

  const min = minClose - paddingY;
  const max = maxClose + paddingY;
  const range = max - min || 1;

  const xFor = (index: number) => {
    return box.left + (index / Math.max(display.length - 1, 1)) * box.width;
  };

  const yFor = (price: number) => {
    return box.top + (1 - (price - min) / range) * box.height;
  };

  const points = display.map<Point>((price, index) => ({
    x: xFor(index),
    y: yFor(price),
    price
  }));

  drawYAxis(ctx, {
    min,
    max,
    range,
    x: box.right + 8,
    top: box.top,
    height: box.height,
    color: text
  });

  const priceColor = quote.direction < 0 || quote.change < 0 ? down : up;

  drawArea(ctx, points, box.bottom, accent);
  drawLine(ctx, points, accent);

  if (hover.active) {
    const hoverPoint = getNearestPoint(points, hover.x, box);

    if (hoverPoint) {
      drawHoverGuide(ctx, {
        point: hoverPoint,
        box,
        color: text,
        accent,
        labelX: box.right + 8
      });
    }
  }

  drawLastPrice(ctx, {
    y: yFor(livePrice),
    price: livePrice,
    color: priceColor,
    textX: box.right + 8,
    lineStartX: box.left,
    lineEndX: box.right
  });

  ctx.restore();
}

function createChartBox(width: number, height: number): ChartBox {
  const padding = {
    top: 30,
    right: 72,
    bottom: 26,
    left: 14
  };

  const left = padding.left;
  const top = padding.top;
  const right = width - padding.right;
  const bottom = height - padding.bottom;

  return {
    left,
    top,
    right,
    bottom,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top)
  };
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  options: { box: ChartBox; color: string }
): void {
  ctx.save();
  ctx.strokeStyle = options.color;
  ctx.globalAlpha = 0.58;
  ctx.lineWidth = 1;

  for (let index = 0; index <= 4; index += 1) {
    const y = options.box.top + (options.box.height / 4) * index;

    ctx.beginPath();
    ctx.moveTo(options.box.left, y);
    ctx.lineTo(options.box.right, y);
    ctx.stroke();
  }

  ctx.restore();
}

function drawYAxis(
  ctx: CanvasRenderingContext2D,
  options: {
    min: number;
    max: number;
    range: number;
    x: number;
    top: number;
    height: number;
    color: string;
  }
): void {
  ctx.save();
  ctx.fillStyle = options.color;
  ctx.font = '600 11px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  for (let index = 0; index <= 4; index += 1) {
    const price = options.max - (options.range / 4) * index;
    const y = options.top + (options.height / 4) * index;

    ctx.fillText(formatNumber(price), options.x, y);
  }

  ctx.restore();
}

function drawArea(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  baseline: number,
  color: string
): void {
  if (points.length < 2) return;

  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.055;

  ctx.beginPath();
  ctx.moveTo(points[0].x, baseline);
  ctx.lineTo(points[0].x, points[0].y);
  addSmoothPath(ctx, points);
  ctx.lineTo(points[points.length - 1].x, baseline);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}

function drawLine(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  color: string
): void {
  if (points.length < 2) return;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.45;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.shadowColor = color;
  ctx.shadowBlur = 5;
  ctx.globalAlpha = 0.98;

  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  addSmoothPath(ctx, points);
  ctx.stroke();

  ctx.restore();
}

function drawLastPrice(
  ctx: CanvasRenderingContext2D,
  options: {
    y: number;
    price: number;
    color: string;
    textX: number;
    lineStartX: number;
    lineEndX: number;
  }
): void {
  ctx.save();

  ctx.strokeStyle = options.color;
  ctx.fillStyle = options.color;
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 4]);
  ctx.globalAlpha = 0.82;

  ctx.beginPath();
  ctx.moveTo(options.lineStartX, options.y);
  ctx.lineTo(options.lineEndX, options.y);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
  ctx.font = '700 12px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(formatNumber(options.price), options.textX, options.y);

  ctx.restore();
}

function drawHoverGuide(
  ctx: CanvasRenderingContext2D,
  options: {
    point: Point;
    box: ChartBox;
    color: string;
    accent: string;
    labelX: number;
  }
): void {
  const { point, box } = options;

  ctx.save();

  ctx.strokeStyle = options.color;
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.46;
  ctx.setLineDash([2, 4]);

  ctx.beginPath();
  ctx.moveTo(point.x, box.top);
  ctx.lineTo(point.x, box.bottom);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(box.left, point.y);
  ctx.lineTo(box.right, point.y);
  ctx.stroke();

  ctx.setLineDash([]);

  ctx.fillStyle = options.accent;
  ctx.strokeStyle = 'rgba(8, 12, 20, 0.95)';
  ctx.globalAlpha = 1;

  ctx.beginPath();
  ctx.arc(point.x, point.y, 3.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.stroke();

  drawPriceBadge(ctx, {
    x: options.labelX,
    y: point.y,
    price: point.price,
    color: options.accent
  });

  ctx.restore();
}

function drawPriceBadge(
  ctx: CanvasRenderingContext2D,
  options: {
    x: number;
    y: number;
    price: number;
    color: string;
  }
): void {
  const text = formatNumber(options.price);
  const width = Math.max(48, ctx.measureText(text).width + 12);
  const height = 19;
  const x = options.x - 2;
  const y = clamp(options.y - height / 2, 4, ctx.canvas.height - height - 4);

  ctx.save();
  ctx.font = '700 11px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  ctx.fillStyle = 'rgba(8, 12, 20, 0.92)';
  roundRect(ctx, x, y, width, height, 5);
  ctx.fill();

  ctx.strokeStyle = options.color;
  ctx.globalAlpha = 0.75;
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.globalAlpha = 1;
  ctx.fillStyle = options.color;
  ctx.fillText(text, x + 6, y + height / 2 + 0.5);

  ctx.restore();
}

function getNearestPoint(
  points: Point[],
  pointerX: number,
  box: ChartBox
): Point | undefined {
  if (points.length === 0) return undefined;

  const x = clamp(pointerX, box.left, box.right);
  const ratio = (x - box.left) / Math.max(box.width, 1);
  const index = Math.round(ratio * (points.length - 1));

  return points[clamp(index, 0, points.length - 1)];
}

function addSmoothPath(ctx: CanvasRenderingContext2D, points: Point[]): void {
  if (points.length < 2) return;

  if (points.length === 2) {
    ctx.lineTo(points[1].x, points[1].y);
    return;
  }

  for (let index = 1; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const midX = (current.x + next.x) / 2;
    const midY = (current.y + next.y) / 2;

    ctx.quadraticCurveTo(current.x, current.y, midX, midY);
  }

  const last = points[points.length - 1];
  ctx.lineTo(last.x, last.y);
}

function smoothCloses(values: number[]): number[] {
  if (values.length < 5) {
    return [...values];
  }

  return values.map((value, index) => {
    if (index === 0 || index === values.length - 1) {
      return value;
    }

    const previous = values[index - 1];
    const next = values[index + 1];

    return previous * 0.25 + value * 0.5 + next * 0.25;
  });
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  const r = Math.min(radius, width / 2, height / 2);

  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}